import React, { useState, useEffect } from 'react';
import { Link, NavLink, useNavigate, useLocation } from 'react-router-dom';
import { LogIn, LogOut, Menu, X, LayoutDashboard, User as UserIcon, ChevronDown } from 'lucide-react';
import { Button } from './ui/button';
import { useAuth } from '../context/AuthContext';
import NotificationsCenter from './NotificationsCenter';

const LOGO_URL = 'https://customer-assets.emergentagent.com/job_inventory-hub-1442/artifacts/xyf6dc2s_logo-tzanetatos-horizontal.png';

const NAV_ITEMS = [
  { to: '/', label: 'Marcas', end: true },
  { to: '/productos', label: 'Productos' },
  { to: '/categorias', label: 'Categorías' },
  { to: '/lo-nuevo', label: 'Lo Nuevo' },
  { to: '/contacto', label: 'Contacto' },
];

const Header = () => {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { isAuthed, email, logout } = useAuth();

  // Close menus on route change
  useEffect(() => {
    setMobileOpen(false);
    setUserMenuOpen(false);
  }, [location.pathname]);

  // Lock body scroll while mobile drawer is open
  useEffect(() => {
    if (mobileOpen) {
      const prev = document.body.style.overflow;
      document.body.style.overflow = 'hidden';
      return () => { document.body.style.overflow = prev; };
    }
    return undefined;
  }, [mobileOpen]);

  // Add subtle shadow when scrolled
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 4);
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  // Close user menu when clicking outside
  useEffect(() => {
    if (!userMenuOpen) return undefined;
    const onClick = (e) => {
      if (!e.target.closest('[data-user-menu]')) setUserMenuOpen(false);
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, [userMenuOpen]);

  const navLinkCls = ({ isActive }) =>
    `relative px-1 py-5 text-sm font-medium tracking-tight transition-colors ${
      isActive ? 'text-emerald-800' : 'text-slate-600 hover:text-emerald-700'
    }`;

  const handleLogout = () => {
    logout();
    setUserMenuOpen(false);
    navigate('/');
  };

  const userInitial = (email || '?').charAt(0).toUpperCase();

  return (
    <>
      <header
        className={`sticky top-0 z-50 w-full border-b border-slate-200 bg-white/95 backdrop-blur-md transition-shadow ${
          scrolled ? 'shadow-sm' : ''
        }`}
      >
        <div className="mx-auto flex h-20 max-w-7xl items-center justify-between px-4 sm:px-6 lg:px-8">
          {/* Logo */}
          <Link to="/" className="flex shrink-0 items-center transition-opacity hover:opacity-90">
            <img
              src={LOGO_URL}
              alt="Catálogo Tzanetatos"
              className="h-11 w-auto object-contain sm:h-12 lg:h-14"
              draggable={false}
            />
          </Link>

          {/* Desktop nav */}
          <nav className="hidden items-center gap-1 md:flex">
            {NAV_ITEMS.map(item => (
              <NavLink key={item.to} to={item.to} end={item.end} className={navLinkCls}>
                {({ isActive }) => (
                  <>
                    <span className="px-3">{item.label}</span>
                    {isActive && (
                      <span className="absolute inset-x-3 -bottom-px h-0.5 rounded-full bg-emerald-600" />
                    )}
                  </>
                )}
              </NavLink>
            ))}
            {isAuthed && (
              <NavLink to="/admin" className={navLinkCls}>
                {({ isActive }) => (
                  <>
                    <span className="px-3">Admin</span>
                    {isActive && (
                      <span className="absolute inset-x-3 -bottom-px h-0.5 rounded-full bg-emerald-600" />
                    )}
                  </>
                )}
              </NavLink>
            )}
          </nav>

          {/* Right side */}
          <div className="flex items-center gap-2">
            <div className="hidden md:block">
              <NotificationsCenter variant="desktop" />
            </div>
            {isAuthed ? (
              <div className="relative hidden md:block" data-user-menu>
                <button
                  type="button"
                  onClick={() => setUserMenuOpen(v => !v)}
                  className="flex items-center gap-2 rounded-full border border-slate-200 bg-white py-1.5 pl-1.5 pr-3 transition-colors hover:border-emerald-300 hover:bg-emerald-50"
                >
                  <span className="flex h-8 w-8 items-center justify-center rounded-full bg-emerald-600 text-sm font-semibold text-white">
                    {userInitial}
                  </span>
                  <span className="max-w-[140px] truncate text-sm font-medium text-slate-700">{email}</span>
                  <ChevronDown className={`h-3.5 w-3.5 text-slate-500 transition-transform ${userMenuOpen ? 'rotate-180' : ''}`} />
                </button>
                {userMenuOpen && (
                  <div className="absolute right-0 top-full mt-2 w-56 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg ring-1 ring-black/5">
                    <div className="border-b border-slate-100 px-4 py-3">
                      <p className="text-xs text-slate-500">Conectado como</p>
                      <p className="truncate text-sm font-medium text-slate-900">{email}</p>
                    </div>
                    <button
                      type="button"
                      onClick={() => { setUserMenuOpen(false); navigate('/admin'); }}
                      className="flex w-full items-center gap-2 px-4 py-2.5 text-sm text-slate-700 transition-colors hover:bg-emerald-50 hover:text-emerald-800"
                    >
                      <LayoutDashboard className="h-4 w-4" /> Panel admin
                    </button>
                    <button
                      type="button"
                      onClick={handleLogout}
                      className="flex w-full items-center gap-2 border-t border-slate-100 px-4 py-2.5 text-sm text-red-600 transition-colors hover:bg-red-50"
                    >
                      <LogOut className="h-4 w-4" /> Cerrar sesión
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Button
                onClick={() => navigate('/login')}
                variant="outline"
                className="hidden h-10 gap-2 border-slate-300 bg-white text-slate-800 hover:border-emerald-300 hover:bg-emerald-50 hover:text-emerald-800 md:inline-flex"
              >
                <LogIn className="h-4 w-4" />
                Iniciar Sesión
              </Button>
            )}

            {/* Mobile toggle */}
            <button
              onClick={() => setMobileOpen(v => !v)}
              className="relative inline-flex h-10 w-10 items-center justify-center rounded-lg text-slate-700 transition-colors hover:bg-slate-100 md:hidden"
              aria-label={mobileOpen ? 'Cerrar menú' : 'Abrir menú'}
              aria-expanded={mobileOpen}
            >
              {mobileOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </header>

      {/* Mobile slide-in panel */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-sm md:hidden"
          onClick={() => setMobileOpen(false)}
          aria-hidden="true"
        />
      )}
      <aside
        className={`fixed inset-y-0 right-0 z-50 flex h-full max-h-screen w-72 max-w-[85vw] flex-col border-l border-slate-200 bg-white shadow-2xl transition-transform duration-300 ease-out md:hidden ${
          mobileOpen ? 'translate-x-0' : 'translate-x-full'
        }`}
        style={{ height: '100dvh' }}
        aria-hidden={!mobileOpen}
      >
        <div className="flex h-20 shrink-0 items-center justify-between border-b border-slate-200 px-5">
          <img src={LOGO_URL} alt="Tzanetatos" className="h-10 w-auto object-contain" />
          <button
            onClick={() => setMobileOpen(false)}
            className="inline-flex h-10 w-10 items-center justify-center rounded-lg text-slate-700 hover:bg-slate-100"
            aria-label="Cerrar menú"
          >
            <X className="h-5 w-5" />
          </button>
        </div>

        {isAuthed && (
          <div className="flex shrink-0 items-center gap-3 border-b border-slate-100 bg-emerald-50/50 px-5 py-4">
            <span className="flex h-10 w-10 items-center justify-center rounded-full bg-emerald-600 text-sm font-semibold text-white">
              {userInitial}
            </span>
            <div className="min-w-0">
              <p className="text-xs text-slate-500">Conectado como</p>
              <p className="truncate text-sm font-medium text-slate-900">{email}</p>
            </div>
          </div>
        )}

        <nav className="flex flex-1 flex-col gap-0.5 overflow-y-auto px-3 py-4">
          <NotificationsCenter variant="drawer" onNavigate={() => setMobileOpen(false)} />
          <div className="my-1 border-t border-slate-100" />
          {NAV_ITEMS.map(item => (
            <NavLink
              key={item.to}
              to={item.to}
              end={item.end}
              className={({ isActive }) =>
                `rounded-lg px-4 py-3 text-base font-medium transition-colors ${
                  isActive
                    ? 'bg-emerald-50 text-emerald-800'
                    : 'text-slate-700 hover:bg-slate-50'
                }`
              }
            >
              {item.label}
            </NavLink>
          ))}
          {isAuthed && (
            <NavLink
              to="/admin"
              className={({ isActive }) =>
                `mt-2 flex items-center gap-2 rounded-lg px-4 py-3 text-base font-medium transition-colors ${
                  isActive
                    ? 'bg-emerald-50 text-emerald-800'
                    : 'text-slate-700 hover:bg-slate-50'
                }`
              }
            >
              <LayoutDashboard className="h-4 w-4" /> Panel admin
            </NavLink>
          )}
        </nav>

        <div className="shrink-0 border-t border-slate-200 bg-white p-4 pb-[max(1rem,env(safe-area-inset-bottom))]">
          {isAuthed ? (
            <Button
              onClick={handleLogout}
              variant="outline"
              className="h-11 w-full justify-center gap-2 border-red-200 text-red-600 hover:bg-red-50 hover:text-red-700"
            >
              <LogOut className="h-4 w-4" /> Cerrar sesión
            </Button>
          ) : (
            <Button
              onClick={() => { setMobileOpen(false); navigate('/login'); }}
              className="h-11 w-full justify-center gap-2 bg-emerald-700 text-white hover:bg-emerald-800"
            >
              <LogIn className="h-4 w-4" /> Iniciar Sesión
            </Button>
          )}
        </div>
      </aside>
    </>
  );
};

export default Header;
